import type { BillingType, SessionsQuery, TransactionStatus } from '@pulsepoint/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { get } from './client';

export interface Session {
  id: string;
  ocppTransactionId: number;
  status: TransactionStatus;
  startedAt: string;
  stoppedAt: string | null;
  durationSec: number;
  energyWh: number;
  powerW: number | null;
  connectorId: number;
  chargerId: string;
  chargerName: string;
  chargePointId: string;
  bay: string | null;
  siteName: string | null;
  organisationName: string | null;
  organisationId: string | null;
  organisationSiteCount: number;
  driverId: string | null;
  driverName: string | null;
  idTag: string | null;
  cardLabel: string | null;
  billingType: BillingType;
  stopReason: string | null;
  pricePerKwh: number | null;
  /** What the energy cost at the session's prices (peak, off-peak…, or a card's own rate). */
  energyCostCents: number | null;
  /** A driver's idle fee (a guest's is in paidCents). */
  idleFeeCents: number | null;
  /** The prices it was charged at, copied when it started. */
  pricing: { timezone: string; periods: { name: string; pricePerKwh: number }[]; idleFeePerMinute: number | null; idleGraceMinutes: number; cardRate?: boolean } | null;
  /** Energy in each period of `pricing`, Wh. */
  periodEnergyWh: number[] | null;
  paidCents: number | null;
  receiptNumber: string | null;
  co2Kg: number | null;
}

export interface SessionDetail extends Session {
  meterStartWh: number;
  meterStopWh: number | null;
  samples: { at: string; energyWh: number | null; powerW: number | null; socPercent: number | null }[];
  rawStart?: unknown;
  rawStop?: unknown;
}

export interface SessionPage {
  rows: Session[];
  total: number;
  energyWh: number;
  co2Kg: number;
  page: number;
  pageSize: number;
}

export interface Dashboard {
  todaySessions: number;
  todayWh: number;
  monthSessions: number;
  monthWh: number;
  lastMonthWh: number;
  monthCo2Kg: number;
  yearCo2Kg: number;
  yearFuelLitres: number;
  timezone: string;
}

export const sessionsKey = ['sessions'] as const;
export const dashboardKey = ['dashboard'] as const;

/** The query string for the list and the export (empty values left out). */
export function sessionsSearch(q: SessionsQuery & Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  return p.toString();
}

export const useSessions = (q: SessionsQuery) =>
  useQuery({ queryKey: [...sessionsKey, 'list', q], queryFn: () => get<SessionPage>(`/sessions?${sessionsSearch(q)}`), placeholderData: keepPreviousData });

export const useSession = (id: string) => useQuery({ queryKey: [...sessionsKey, 'one', id], queryFn: () => get<SessionDetail>(`/sessions/${id}`) });

export const localTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Sydney';

export const useDashboard = () =>
  useQuery({ queryKey: [...dashboardKey, localTimezone()], queryFn: () => get<Dashboard>(`/dashboard?tz=${encodeURIComponent(localTimezone())}`) });

/** Energy from charging sessions per month: this month and the 12 before, oldest first. */
export const useDashboardMonths = () =>
  useQuery({
    queryKey: [...dashboardKey, 'months', localTimezone()],
    queryFn: () => get<{ months: { month: string; wh: number; sessions: number }[] }>(`/dashboard/months?tz=${encodeURIComponent(localTimezone())}`),
    staleTime: 5 * 60_000,
  });
